import React, { useState, useEffect } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { QuestionRenderer } from "@/components/QuestionRenderer";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import { 
  Heart, Home, MapPin, DollarSign, ChevronRight, ChevronLeft,
  CheckCircle, SkipForward, Sparkles, AlertCircle, Info
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL || ''}/api`;

// Wave configuration
const waveConfig = {
  1: {
    title: "ADN Emocional",
    subtitle: "Sensaciones y estilo de vida",
    icon: Heart,
    color: "text-rose-500",
    bgColor: "bg-rose-50",
    category: "emotional"
  },
  2: {
    title: "Arquitectura y Técnica",
    subtitle: "Características físicas",
    icon: Home,
    color: "text-blue-500",
    bgColor: "bg-blue-50",
    category: "technical"
  },
  3: {
    title: "Entorno y Urbanismo",
    subtitle: "El vecindario y la zona",
    icon: MapPin,
    color: "text-green-500",
    bgColor: "bg-green-50",
    category: "urban"
  },
  4: {
    title: "Viabilidad Financiera",
    subtitle: "Aspectos económicos",
    icon: DollarSign,
    color: "text-amber-500",
    bgColor: "bg-amber-50",
    category: "financial"
  }
};

// Simplified questions for property owners (reframed from buyer perspective to property perspective)
const getPropertyQuestions = () => ({
  emotional: [
    { id: "e1", text: "¿Cómo entra el sol a la propiedad?", type: "choice", options: ["Recibe el amanecer ☀️", "Recibe el atardecer 🌅", "Sombra predominante 🌙"], wave: 1 },
    { id: "e2", text: "Nivel de ruido urbano de la zona:", type: "scale", min: 1, max: 10, labels: ["Silencio", "Zona animada"], wave: 1 },
    { id: "e3", text: "¿Tiene espacio para estación de café o desayunador?", type: "choice", options: ["Sí, área dedicada ☕", "Desayunador con vista", "Cocina básica"], wave: 1 },
    { id: "e4", text: "Aromas cercanos a la propiedad:", type: "choice", options: ["Panaderías 🥖", "Parques y naturaleza 🌳", "Zona residencial sin comercios"], wave: 1 },
    { id: "e5", text: "Acabados predominantes:", type: "choice", options: ["Madera cálida 🪵", "Mármol y piedra", "Microcemento industrial"], wave: 1 },
    { id: "e6", text: "¿Tiene rincón de lectura o estudio?", type: "boolean", wave: 1 },
    { id: "e7", text: "¿Hay espacio para yoga/meditación?", type: "boolean", wave: 1 },
    { id: "e8", text: "¿La cocina es abierta para convivir?", type: "choice", options: ["Sí, cocina abierta 👨‍👩‍👧", "Cocina cerrada/separada", "Semi-abierta"], wave: 1 },
    { id: "e9", text: "Estilo arquitectónico:", type: "choice", options: ["Ladrillo visto, vigas 🏛️", "Moderno minimalista ✨", "Mezcla de estilos"], wave: 1 },
    { id: "e10", text: "Conexión con naturaleza (jardín, terraza, vistas):", type: "scale", min: 1, max: 10, labels: ["Mínima", "Excelente"], wave: 1 },
    { id: "e11", text: "¿La propiedad es pet-friendly?", type: "choice", options: ["Ideal para perros 🐕", "Ideal para gatos 🐱", "Para mascotas pequeñas", "No recomendada"], wave: 1 },
    { id: "e12", text: "Nivel de privacidad visual:", type: "scale", min: 1, max: 10, labels: ["Vista compartida", "Privacidad total"], wave: 1 },
    { id: "e13", text: "Domótica instalada:", type: "choice", options: ["Smart home completo 📱", "Sistema tradicional", "Híbrido"], wave: 1 },
    { id: "e14", text: "Altura de techos:", type: "choice", options: ["Techos altos (3m+) 🕊️", "Estándar (2.4-2.7m)", "Acogedores (menos de 2.4m)"], wave: 1 },
    { id: "e15", text: "¿Tiene algún 'Wow Factor'?", type: "choice", options: ["Sí, elemento único ✨", "Diseño funcional", "Estándar"], wave: 1 }
  ],
  technical: [
    { id: "t1", text: "Distribución de espacios:", type: "choice", options: ["Pasillos amplios y fluidos", "M² optimizados", "Equilibrado"], wave: 2 },
    { id: "t2", text: "Almacenamiento disponible:", type: "scale", min: 1, max: 10, labels: ["Básico", "Amplio + bodega"], wave: 2 },
    { id: "t3", text: "Espacio para home office:", type: "choice", options: ["Oficina dedicada 💼", "Adaptable en recámara", "Sin espacio"], wave: 2 },
    { id: "t4", text: "Sistema de ventilación:", type: "choice", options: ["Ventilación cruzada 🌬️", "A/C instalado", "Combinación"], wave: 2 },
    { id: "t5", text: "Aislamiento acústico entre cuartos:", type: "scale", min: 1, max: 10, labels: ["Básico", "Excelente"], wave: 2 },
    { id: "t6", text: "¿Tiene cuarto de lavado o mudroom?", type: "boolean", wave: 2 },
    { id: "t7", text: "Preparación para auto eléctrico:", type: "choice", options: ["Cargador instalado 🔌", "Preparación eléctrica", "No disponible"], wave: 2 },
    { id: "t8", text: "Calidad del sistema de agua:", type: "choice", options: ["Excelente presión, agua caliente rápida 🚿", "Estándar", "Básico"], wave: 2 },
    { id: "t9", text: "Eficiencia energética:", type: "choice", options: ["Paneles solares ☀️", "Calentador solar", "Sistema tradicional"], wave: 2 },
    { id: "t10", text: "¿Muros modificables?", type: "boolean", wave: 2 },
    { id: "t11", text: "Tipo de iluminación:", type: "choice", options: ["LED cálido/foseado 💡", "Focos tradicionales", "Combinación"], wave: 2 },
    { id: "t12", text: "Sistema de seguridad:", type: "choice", options: ["Cámaras y alarmas 📹", "Básico", "Sin sistema"], wave: 2 }
  ],
  urban: [
    { id: "u1", text: "Distancia a cafeterías/comercios:", type: "choice", options: ["A la vuelta ☕", "5-10 min caminando", "Requiere auto"], wave: 3 },
    { id: "u2", text: "Seguridad del vecindario:", type: "scale", min: 1, max: 10, labels: ["Básica", "Vigilancia 24/7"], wave: 3 },
    { id: "u3", text: "Cercanía a parques/gimnasios:", type: "choice", options: ["Muy cerca 🏃", "Accesible", "Lejos"], wave: 3 },
    { id: "u4", text: "Acceso para apps de delivery:", type: "scale", min: 1, max: 10, labels: ["Difícil", "Excelente"], wave: 3 },
    { id: "u5", text: "Tiempo de salida en hora pico:", type: "choice", options: ["Menos de 15 min 🚗", "15-30 min", "30+ min"], wave: 3 },
    { id: "u6", text: "Perfil del vecindario:", type: "choice", options: ["Familias jóvenes 👨‍👩‍👧", "Diverso", "Adultos mayores"], wave: 3 },
    { id: "u7", text: "Contaminación visual (cables, postes):", type: "choice", options: ["Servicios subterráneos", "Normal", "Visible"], wave: 3 },
    { id: "u8", text: "Cercanía a parques pet-friendly:", type: "choice", options: ["Menos de 5 min 🐕", "5-15 min", "Lejos"], wave: 3 },
    { id: "u9", text: "Oferta gastronómica y cultural:", type: "scale", min: 1, max: 10, labels: ["Básica", "Amplia"], wave: 3 },
    { id: "u10", text: "Calidad de internet disponible:", type: "choice", options: ["Fibra óptica 🌐", "Internet estándar", "Limitado"], wave: 3 }
  ],
  financial: [
    { id: "f1", text: "Rango de precio de la propiedad (MXN):", type: "choice", options: ["0-2M", "2-5M", "5-10M", "10M+"], wave: 4 },
    { id: "f2", text: "Mantenimiento mensual estimado:", type: "choice", options: ["Menos de 5K", "5-10K", "10-20K", "20K+"], wave: 4 },
    { id: "f3", text: "Facilidades de crédito que acepta:", type: "choice", options: ["Cualquier financiamiento 💳", "Solo bancario", "Contado únicamente"], wave: 4 },
    { id: "f4", text: "Perfil ideal del comprador:", type: "choice", options: ["Inversionista 📈", "Vivienda principal 🏠", "Cualquiera"], wave: 4 },
    { id: "f5", text: "Proyección de plusvalía de la zona:", type: "scale", min: 1, max: 10, labels: ["Estable", "Alta plusvalía"], wave: 4 },
    { id: "f6", text: "Facilidad de reventa:", type: "choice", options: ["Muy demandada", "Moderada", "Nicho específico"], wave: 4 },
    { id: "f7", text: "¿Es apta para Airbnb?", type: "choice", options: ["Sí, excelente ubicación", "Posiblemente", "No recomendada"], wave: 4 },
    { id: "f8", text: "Estado de la propiedad:", type: "choice", options: ["Lista para habitar ✅", "Requiere mejoras menores", "Requiere remodelación"], wave: 4 },
    { id: "f9", text: "Predial anual aproximado:", type: "choice", options: ["Menos de 10K", "10-30K", "30K+"], wave: 4 },
    { id: "f10", text: "Tipos de pago aceptados:", type: "choice", options: ["Crédito bancario", "Infonavit/Fovissste", "Contado", "Varios"], wave: 4 },
    { id: "f11", text: "¿Incluye seguro de la propiedad?", type: "boolean", wave: 4 },
    { id: "f12", text: "Flexibilidad de negociación:", type: "choice", options: ["Precio fijo", "Negociable", "Acepta permuta"], wave: 4 },
    { id: "f13", text: "¿Documentación al día?", type: "boolean", wave: 4 },
    { id: "f14", text: "Cuotas de mantenimiento extraordinarias:", type: "choice", options: ["No hay pendientes", "Posibles a futuro", "Hay cuotas pendientes"], wave: 4 },
    { id: "f15", text: "Principal atractivo de la propiedad:", type: "choice", options: ["Calidad de vida 😊", "Inversión/ROI 💰", "Ambos"], wave: 4 }
  ]
});

// Question item component
const QuestionItem = ({ question, answer, onAnswer, onSkip, disabled }) => {
  const isAnswered = answer !== undefined && answer !== null;
  const isSkipped = answer === "skipped";

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className={`p-4 rounded-lg border ${isAnswered ? (isSkipped ? "border-slate-200 bg-slate-50" : "border-green-200 bg-green-50") : "border-slate-200"}`}
    >
      <div className="flex items-start justify-between mb-3">
        <p className="font-medium text-[#384451] flex-1">{question.text}</p>
        {isAnswered && !isSkipped && (
          <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 ml-2" />
        )}
        {isSkipped && (
          <SkipForward className="w-5 h-5 text-slate-400 flex-shrink-0 ml-2" />
        )}
      </div>

      <QuestionRenderer
        question={question}
        value={isSkipped ? null : answer}
        onChange={(value) => onAnswer(question.id, value)}
        disabled={disabled}
      />

      {!isAnswered && (
        <Button
          variant="ghost"
          size="sm"
          className="mt-2 text-slate-400 hover:text-slate-600"
          onClick={() => onSkip(question.id)}
        >
          <SkipForward className="w-4 h-4 mr-1" /> Omitir
        </Button>
      )}
    </motion.div>
  );
};

// Main Property Questionnaire Component
export const PropertyQuestionnaire = ({ 
  mode = "owner", // "owner" or "agent"
  propertyId = null, // For agent editing existing property
  onComplete, 
  onSkipAll,
  initialAnswers = {}
}) => {
  const [questions] = useState(getPropertyQuestions());
  const [currentWave, setCurrentWave] = useState(1);
  const [answers, setAnswers] = useState(initialAnswers);
  const [saving, setSaving] = useState(false);

  const totalQuestions = Object.values(questions).flat().length;
  const answeredQuestions = Object.keys(answers).filter(k => answers[k] !== undefined).length;
  const progress = (answeredQuestions / totalQuestions) * 100;

  const currentCategory = waveConfig[currentWave].category;
  const currentQuestions = questions[currentCategory] || [];
  const currentAnswered = currentQuestions.filter(q => answers[q.id] !== undefined).length;

  const handleAnswer = (questionId, value) => {
    setAnswers(prev => ({ ...prev, [questionId]: value }));
  };

  const handleSkip = (questionId) => {
    setAnswers(prev => ({ ...prev, [questionId]: "skipped" }));
  };

  const handleSkipWave = () => {
    const skipped = {};
    currentQuestions.forEach(q => {
      if (answers[q.id] === undefined) {
        skipped[q.id] = "skipped";
      }
    });
    setAnswers(prev => ({ ...prev, ...skipped }));
    
    if (currentWave < 4) {
      setCurrentWave(currentWave + 1);
    }
  };

  const handleComplete = async () => {
    setSaving(true);
    try {
      // Convert answers to psychographic attributes format
      const psychographicAttributes = {
        emotional: {},
        technical: {},
        urban: {},
        financial: {}
      };

      Object.entries(answers).forEach(([questionId, value]) => {
        if (value === "skipped") return;
        
        if (questionId.startsWith("e")) {
          psychographicAttributes.emotional[questionId] = value;
        } else if (questionId.startsWith("t")) {
          psychographicAttributes.technical[questionId] = value;
        } else if (questionId.startsWith("u")) {
          psychographicAttributes.urban[questionId] = value;
        } else if (questionId.startsWith("f")) {
          psychographicAttributes.financial[questionId] = value;
        }
      });

      if (onComplete) {
        await onComplete(psychographicAttributes);
      }
      
      toast.success("¡Cuestionario guardado exitosamente!");
    } catch (error) {
      toast.error("Error al guardar el cuestionario");
    } finally {
      setSaving(false);
    }
  };

  const WaveIcon = waveConfig[currentWave].icon;

  return (
    <div className="space-y-6">
      {/* Progress Header */}
      <Card className="border-0 bg-gradient-to-r from-primary/10 to-primary/5">
        <CardContent className="p-4">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-3">
              <div className={`w-10 h-10 rounded-full ${waveConfig[currentWave].bgColor} flex items-center justify-center`}>
                <WaveIcon className={`w-5 h-5 ${waveConfig[currentWave].color}`} />
              </div>
              <div>
                <h3 className="font-semibold text-[#384451]">{waveConfig[currentWave].title}</h3>
                <p className="text-sm text-slate-500">{waveConfig[currentWave].subtitle}</p>
              </div>
            </div>
            <Badge variant="outline" className="text-primary border-primary">
              {answeredQuestions}/{totalQuestions} respondidas
            </Badge>
          </div>
          <Progress value={progress} className="h-2" />
        </CardContent>
      </Card>

      {/* Info Banner */}
      <div className="flex items-start gap-3 p-4 bg-blue-50 border border-blue-200 rounded-lg">
        <Info className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
        <div>
          <p className="text-sm text-blue-800 font-medium">Este cuestionario es opcional</p>
          <p className="text-sm text-blue-600">
            Completarlo mejora el matching con compradores. Si lo omites, el agente podrá completarlo después.
          </p>
        </div>
      </div>

      {/* Wave Tabs */}
      <div className="flex gap-2 overflow-x-auto pb-2">
        {[1, 2, 3, 4].map(wave => {
          const config = waveConfig[wave];
          const Icon = config.icon;
          const waveQuestions = questions[config.category] || [];
          const waveAnswered = waveQuestions.filter(q => answers[q.id] !== undefined).length;
          const isComplete = waveAnswered === waveQuestions.length;
          
          return (
            <Button
              key={wave}
              variant={currentWave === wave ? "default" : "outline"}
              className={`flex-shrink-0 gap-2 ${currentWave === wave ? "bg-primary text-[#384451]" : ""}`}
              onClick={() => setCurrentWave(wave)}
            >
              <Icon className="w-4 h-4" />
              <span className="hidden sm:inline">{config.title.split(":")[0]}</span>
              {isComplete && <CheckCircle className="w-4 h-4 text-green-500" />}
              {!isComplete && waveAnswered > 0 && (
                <span className="text-xs opacity-70">{waveAnswered}/{waveQuestions.length}</span>
              )}
            </Button>
          );
        })}
      </div>

      {/* Questions */}
      <ScrollArea className="h-[400px] pr-4">
        <div className="space-y-4">
          {currentQuestions.map(question => (
            <QuestionItem
              key={question.id}
              question={question}
              answer={answers[question.id]}
              onAnswer={handleAnswer}
              onSkip={handleSkip}
              disabled={saving}
            />
          ))}
        </div>
      </ScrollArea>

      {/* Navigation */}
      <div className="flex items-center justify-between pt-4 border-t">
        <div className="flex gap-2">
          {currentWave > 1 && (
            <Button variant="outline" onClick={() => setCurrentWave(currentWave - 1)}>
              <ChevronLeft className="w-4 h-4 mr-1" /> Anterior
            </Button>
          )}
          {onSkipAll && (
            <Button variant="ghost" onClick={onSkipAll} className="text-slate-500">
              Omitir todo el cuestionario
            </Button>
          )}
        </div>

        <div className="flex gap-2">
          {currentWave < 4 ? (
            <>
              <Button variant="outline" onClick={handleSkipWave}>
                Omitir esta sección
              </Button>
              <Button 
                onClick={() => setCurrentWave(currentWave + 1)}
                className="bg-primary text-[#384451]"
              >
                Siguiente <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            </>
          ) : (
            <Button 
              onClick={handleComplete}
              disabled={saving}
              className="bg-primary text-[#384451]"
            >
              {saving ? (
                <>Guardando...</>
              ) : (
                <>
                  <Sparkles className="w-4 h-4 mr-2" />
                  Completar Cuestionario
                </>
              )}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default PropertyQuestionnaire;
